/**
 * Document Detail View Route
 *
 * Renders the document detail sheet as a full overlay.
 * Navigating to `/$workspaceSlug/documents/:id` opens the sheet; closing it navigates back.
 */

import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useDocumentById } from "@/hooks/use-documents";
import { DocumentDetailSheet } from "@/components/documents";
import { useChatContext } from "@/stores/chat-context-store";
import { toDocumentId } from "@/lib/route-params";

function DocumentDetailRoute() {
  const { documentId: documentIdParam, workspaceSlug } = Route.useParams();
  const documentId = toDocumentId(documentIdParam);
  const navigate = useNavigate();
  const { document } = useDocumentById(documentId);
  const addEntity = useChatContext((s) => s.addEntity);

  // Auto-add opened document to chat entity context
  useEffect(() => {
    if (document) {
      addEntity({
        type: "document",
        id: document.id,
        label: document.title,
      });
    }
  }, [document, addEntity]);

  const handleClose = () => {
    navigate({
      to: "/$workspaceSlug/documents",
      params: { workspaceSlug },
    });
  };

  return (
    <DocumentDetailSheet
      open={true}
      onClose={handleClose}
      document={document ?? null}
    />
  );
}

export const Route = createFileRoute("/$workspaceSlug/documents/$documentId")({
  component: DocumentDetailRoute,
});
